import React from 'react'

/** Shared across GGC dataset overview + release copy */
export function GgcDatasetIntro() {
  return (
    <p>
      The Global Growth Cube is a comprehensive global asset management market-sizing database, providing granular historical and forward-looking views of asset and revenue pools and net flows by geography, client segment, and product (asset class and vehicle).
    </p>
  )
}

/** Full granularity (29 markets) — Release Information geography list */
export function FullGranularityGeographicRegions() {
  return (
    <>
      <p><strong>North America:</strong> Canada, United States</p>
      <p><strong>LATAM:</strong> Brazil, Chile, Mexico</p>
      <p><strong>Western Europe:</strong> Austria, France, Germany, Italy, Netherlands, Denmark, Finland, Norway, Sweden, Spain, Switzerland, United Kingdom</p>
      <p><strong>Middle East &amp; Africa:</strong> GCC Region</p>
      <p><strong>East Asia:</strong> China, Hong Kong, Japan, South Korea, Taiwan</p>
      <p><strong>Southeast Asia:</strong> Indonesia, Malaysia, Singapore, Thailand</p>
      <p><strong>Rest of Asia-Pacific:</strong> India, Australia</p>
    </>
  )
}
export function FullGranularityPreviousReleaseGeographicRegions() {
  return (
    <>
      <p><strong>North America:</strong> Canada, United States</p>
      <p><strong>LATAM:</strong> Brazil, Chile, Mexico</p>
      <p><strong>Western Europe:</strong> Austria, France, Germany, Italy, Netherlands, Denmark, Finland, Norway, Sweden, Spain, Switzerland, United Kingdom</p>
      <p><strong>Middle East &amp; Africa:</strong> GCC Region</p>
      <p><strong>East Asia:</strong> China, Hong Kong, Japan, South Korea, Taiwan</p>
      <p><strong>Southeast Asia:</strong> Indonesia, Malaysia, Singapore, Thailand</p>
      <p><strong>Rest of Asia-Pacific:</strong> India, Australia</p>
    </>
  )
}

/** Segment + Total Financial (42 markets) — Previous Release Information geography list */
export function FortyTwoMarketPreviousGeographicRegions() {
  return (
    <>
      <p><strong>North America:</strong> Canada, United States</p>
      <p><strong>LATAM:</strong> Argentina, Brazil, Chile, Mexico</p>
      <p><strong>Western Europe:</strong> Austria, Belgium, France, Germany, Ireland, Italy, Luxembourg, Netherlands, Denmark, Finland, Norway, Portugal, Sweden, Spain, Switzerland, United Kingdom</p>
      <p><strong>Central &amp; Eastern Europe:</strong> Czech Republic, Hungary, Poland, Russia, Slovakia, Ukraine</p>
      <p><strong>Middle East &amp; Africa:</strong> GCC Region, Israel, South Africa</p>
      <p><strong>East Asia:</strong> China, Hong Kong, Japan, South Korea, Taiwan</p>
      <p><strong>Southeast Asia:</strong> Indonesia, Malaysia, Singapore, Thailand</p>
      <p><strong>Rest of Asia-Pacific:</strong> India, Australia</p>
    </>
  )
}

export function FortyTwoMarketGeographicRegionsSegmentLatest() {
  return (
    <>
      <p><strong>North America:</strong> Canada, United States</p>
      <p><strong>LATAM:</strong> Argentina, Brazil, Chile, Mexico</p>
      <p><strong>Western Europe:</strong> Austria, Belgium, France, Germany, Ireland, Italy, Luxembourg, Netherlands, Denmark, Finland, Norway, Sweden, Portugal, Spain, Switzerland, United Kingdom</p>
      <p><strong>Central &amp; Eastern Europe:</strong> Czech Republic, Hungary, Poland, Russia, Slovakia, Ukraine </p>
      <p><strong>Middle East &amp; Africa:</strong> GCC Region, Israel, South Africa</p>
      <p><strong>East Asia:</strong> China, Hong Kong, Japan, South Korea, Taiwan</p>
      <p><strong>Southeast Asia:</strong> Indonesia, Malaysia, Singapore, Thailand</p>
      <p><strong>Rest of Asia-Pacific:</strong> India, Australia</p>
    </>
  )
}
export function FortyTwoMarketGeographicRegionsTFALatest() {
  return (
    <>
      <p><strong>North America:</strong> Canada, United States</p>
      <p><strong>LATAM:</strong> Argentina, Brazil, Chile, Mexico</p>
      <p><strong>Western Europe:</strong> Austria, Belgium, France, Germany, Ireland, Italy, Luxembourg, Netherlands, Denmark, Finland, Norway, Sweden, Portugal, Spain, Switzerland, United Kingdom</p>
      <p><strong>Central &amp; Eastern Europe:</strong> Czech Republic, Hungary, Poland, Russia, Slovakia, Ukraine</p>
      <p><strong>Middle East &amp; Africa:</strong> GCC Region, Israel, South Africa</p>
      <p><strong>East Asia:</strong> China, Hong Kong, Japan, South Korea, Taiwan</p>
      <p><strong>Southeast Asia:</strong> Indonesia, Malaysia, Singapore, Thailand</p>
      <p><strong>Rest of Asia-Pacific:</strong> India, Australia</p>
    </>
  )
}

export function ReleaseContentsPreviousHeader() {
  return (
    <>
      <h1>Contents of previous release (as of June, 2025)</h1>
      <h2>Global / Overall</h2>
      <p>As part of the annual data update, historical years may have been revised due to restatements of inputs from underlying individual sources</p>
      <p>Country-by-country details of 2023 restatements and adjustments are summarized below</p>
    </>
  )
}

export function ReleaseContentsLatestGlobalHeader({ omitTrailingPeriods, globalHeading = 'Global / Overall' }) {
  const a = 'As part of the annual data update, historical years may have been revised due to restatements of inputs from underlying individual sources'
  const b = 'Country-by-country details of 2024 restatements and adjustments are summarized below'
  return (
    <>
      <h1>Contents of latest release (as of June, 2026)</h1>
      <h2>{globalHeading}</h2>
      <p>{omitTrailingPeriods ? a : `${a}.`}</p>
      <p>{omitTrailingPeriods ? b : `${b}.`}</p>
    </>
  )
}

/** Full granularity — latest release country adjustments */
export function FullGranularityLatestCountryDetails() {
  return (
    <>
      <h2>Western Europe</h2>
      <h3>France</h3>
      <p><strong>Retail & HNW:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in an increase of ~$200 bn for 2024</p>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in an increase of ~$70 bn for 2024</p>
      <p><strong>Defined Contribution:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in a decrease of ~$100 bn for 2024</p>
      <p><strong>Insurance Companies:</strong> Restated AUM based on latest available data from the Banque de France (BdF) and the European Insurance and Occupational Pensions Authority (EIOPA) to remove unit-linked assets already accounted for in Retail & HNW, resulting in a decrease of ~$500 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$235 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$125 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$115 bn for 2024</p>
      <h3>Italy</h3>
      <p><strong>Insurance Companies:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$20 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$5 bn for 2024</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in an increase of ~$60 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$70 bn for 2024</p>
      <h3>South Korea</h3>
      <p><strong>SWF:</strong> Revised AUM based on latest available data from the Korea Investment Corporation (KIC) resulting in an increase of ~$14 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$22 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$15 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$21bn for 2024</p>
      <h2>Southeast Asia</h2>
      <h3>Singapore</h3>
      <p><strong>State Entities: </strong> Revised AUM based on latest available data from the Monetary Authority of Singapore (MAS) resulting in a decrease of ~$11 bn for 2024</p>
       </>
  )
}
/** Full granularity — previous release country adjustments */
export function FullGranularityPreviousCountryDetails() {
  return (
    <>
      <h2>North America</h2>
      <h3>United States</h3>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in a decrease of ~$600 bn for 2023</p>
      <p><strong>Defined Contribution:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in an increase of ~$570 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in a decrease of ~$130 bn for 2023</p>
      <h2>Western Europe</h2>
      <h3>Germany</h3>
      <p><strong>State Entities:</strong> Restated AUM based on latest available data from the Deutsche Bundesbank, resulting in a decrease of ~$270 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Restated AUM based on latest available data from the Deutsche Bundesbank, resulting in an increase of ~$270 bn for 2023</p>
      <h3>Italy</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$35 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$70 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2023</p>
      <h3>Netherlands</h3>
      <p><strong>Insurance Companies:</strong> Revised AUM based on latest available data from Statistics Netherlands (CBS), resulting in a decrease of ~$10 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from Statistics Netherlands (CBS), resulting in an increase of ~$20 bn for 2023</p>
      <h3>Spain</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the National Securities Market Commission (CNMV), resulting in an increase of ~$10 bn for 2023</p>
      <h3>Switzerland</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from Swiss Fund Data (SFD), resulting in an increase of ~$10 bn for 2023</p>
      <h3>United Kingdom</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data for retail mutual funds from Broadridge Global Market Intelligence, resulting in a decrease of ~$75 bn for 2023</p>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Office for National Statistics (ONS), resulting in an increase of ~$85 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Restated AUM to exclude non-addressable assets based on latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$250 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Restated AUM to exclude non-addressable assets based on the latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$90 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised AUM based on latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$30 bn for 2023</p>
      <h2>Middle East &amp; Africa</h2>
      <h3>GCC Region</h3>
      <p><strong>Sovereign Wealth Funds:</strong> Revised AUM based on latest outsourcing statistics from the Abu Dhabi Investment Authority (ADIA), resulting in a decrease of ~$110 bn for 2023</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC) and WIND, resulting in a decrease of ~$250 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$60 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$70 bn for 2023</p>
      <h3>Japan</h3>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Government Pension Investment Fund (GPIF) and Pension Fund Association (PFA), resulting in an increase of ~$100 bn for 2023</p>
    </>
  )
}

/** Segment granularity — previous release country adjustments */
export function SegmentGranularityPreviousCountryDetails() {
  return (
    <>
      <h2>North America</h2>
      <h3>United States</h3>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in a decrease of ~$600 bn for 2023</p>
      <p><strong>Defined Contribution:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in an increase of ~$570 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Federal Reserve, resulting in a decrease of ~$130 bn for 2023</p>
      <h2>Western Europe</h2>
      <h3>Germany</h3>
      <p><strong>State Entities:</strong> Restated AUM based on latest available data from the Deutsche Bundesbank, resulting in a decrease of ~$270 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Restated AUM based on latest available data from the Deutsche Bundesbank, resulting in an increase of ~$270 bn for 2023</p>
      <h3>Italy</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$35 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$70 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2023</p>
      <h3>Netherlands</h3>
      <p><strong>Insurance Companies:</strong> Revised AUM based on latest available data from Statistics Netherlands (CBS), resulting in a decrease of ~$10 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from Statistics Netherlands (CBS), resulting in an increase of ~$20 bn for 2023</p>
      <h3>Spain</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the National Securities Market Commission (CNMV), resulting in an increase of ~$10 bn for 2023</p>
      <h3>Sweden</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from Statistics Sweden (SCB), resulting in an increase of ~$15 bn for 2023</p>
      <h3>Switzerland</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from Swiss Fund Data (SFD), resulting in an increase of ~$10 bn for 2023</p>
      <h3>United Kingdom</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data for retail mutual funds from Broadridge Global Market Intelligence, resulting in a decrease of ~$75 bn for 2023</p>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Office for National Statistics (ONS), resulting in an increase of ~$85 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Restated AUM to exclude non-addressable assets based on latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$250 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Restated AUM to exclude non-addressable assets based on latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$90 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised AUM based on latest available data from the Office for National Statistics (ONS), resulting in a decrease of ~$30 bn for 2023</p>
      <h2>Middle East &amp; Africa</h2>
      <h3>GCC Region</h3>
      <p><strong>Sovereign Wealth Funds:</strong> Revised AUM based on latest outsourcing statistics from the Abu Dhabi Investment Authority (ADIA), resulting in a decrease of ~$110 bn for 2023</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Retail &amp; HNW:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC) and WIND, resulting in a decrease of ~$250 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$60 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$70 bn for 2023</p>
      <h3>Japan</h3>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Government Pension Investment Fund (GPIF) and Pension Fund Association (PFA), resulting in an increase of ~$100 bn for 2023</p>
    </>
  )
}
/** Segment granularity — latest release country adjustments */
export function SegmentGranularityLatestCountryDetails() {
  return (
    <>
      <h2>Western Europe</h2>
      <h3>France</h3>
      <p><strong>Retail & HNW:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in an increase of ~$200 bn for 2024</p>
      <p><strong>Defined Benefit:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in an increase of ~$70 bn for 2024</p>
      <p><strong>Defined Contribution:</strong> Revised AUM based on latest available data from the Banque de France (BdF), resulting in a decrease of ~$100 bn for 2024</p>
      <p><strong>Insurance Companies:</strong> Restated AUM based on latest available data from the Banque de France (BdF) and the European Insurance and Occupational Pensions Authority (EIOPA) to remove unit-linked assets already accounted for in Retail & HNW, resulting in a decrease of ~$500 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$235 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$125 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised AUM based on latest available data from the Banque de France, resulting in a decrease of ~$115 bn for 2024</p>
      <h3>Italy</h3>
      <p><strong>Insurance Companies:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$20 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Central Bank of Italy, resulting in an increase of ~$5 bn for 2024</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in an increase of ~$60 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$70 bn for 2024</p>
      <h3>South Korea</h3>
      <p><strong>SWF:</strong> Revised AUM based on latest available data from the Korea Investment Corporation (KIC) resulting in an increase of ~$14 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$22 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$15 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised AUM based on latest available data from the Bank of Korea (BOK) resulting in an increase of ~$21bn for 2024</p>
      <h2>South East Asia</h2>
      <h3>Singapore</h3>
      <p><strong>State Entities: </strong> Revised AUM based on latest available data from the Monetary Authority of Singapore (MAS) resulting in a decrease of ~$11 bn for 2024</p>
      </>
  )
}

export function GgcPreviousReleaseInfoBlock({ introText, metricsLine, regions, previousCountryDetails }) {
  return (
    <>
      <p>{introText}</p>
      {regions}
      {metricsLine}
      <ReleaseContentsPreviousHeader />
      {previousCountryDetails}
    </>
  )
}

export function GgcLatestAumReleaseInfoBlock({
  introText,
  metricsLine,
  omitTrailingPeriodsOnGlobal,
  regions,
  latestCountryDetails,
  globalHeading = 'Global / Overall',
}) {
  return (
    <>
      <p>{introText}</p>
      {regions}
      {metricsLine}
      <ReleaseContentsLatestGlobalHeader
        omitTrailingPeriods={omitTrailingPeriodsOnGlobal}
        globalHeading={globalHeading}
      />
      {latestCountryDetails}
    </>
  )
}

export const INTRO_29_MARKETS = 'The following 29 markets have been updated with historical AUM, net flows and revenues:'
export const INTRO_6_MARKETS = 'The following 29 markets have been updated with historical AUM, net flows and revenues:'
export const INTRO_42_AUM_NET_FLOWS = 'The following 42 markets have been updated with historical AUM and net flows:'
export const INTRO_LATEST_AUM_NET_FLOWS = 'The following 42 markets have been updated with historical AUM and net flows:'
export const INTRO_42_MARKETS_UPDATED = 'The following 42 markets have been updated:'
export const INTRO_LATEST_MARKETS_UPDATED = 'The following 42 markets have been updated with historical AUM, net flows and revenues:'

export const METRICS_PREVIOUS_FULL_GRANULARITY = (
  <p><strong>Metrics:</strong> 2007–2024 AUM, net flows and revenues by geography, client segment, vehicle and asset class</p>
)
export const METRICS_FULL_GRANULARITY = (
  <p><strong>Metrics:</strong> 2007-2025 AUM, net flows and revenues by geography, client segment, vehicle and asset class</p>
)
export const METRICS_PREVIOUS_SEGMENT = (
  <p><strong>Metrics:</strong> 2007–2024 AUM and net flows by geography and high-level client segment</p>
)
export const METRICS_SEGMENT = (
  <p><strong>Metrics:</strong> 2007-2025 AUM, net flows and revenues by geography, client segment, vehicle and asset class</p>
)
export const METRICS_TFA = (
  <p><strong>Metrics:</strong> 2007–2024 AUM and TFA by geography and client segment</p>
)

export function TfaLatestReleaseInfoBlock() {
  return (
    <>
      <p>{INTRO_LATEST_MARKETS_UPDATED}</p>
      <FortyTwoMarketGeographicRegionsTFALatest />
      <p><strong>Metrics:</strong> 2007-2025 AUM and TFA by geography and client segment</p>
      <ReleaseContentsLatestGlobalHeader omitTrailingPeriods globalHeading="Global/Overall" />
      <TfaLatestReleaseCountryDetails />
    </>
  )
}
export function TfaLatestReleaseCountryDetails() {
  return (
    <>
      <h2>Western Europe</h2>
      <h3>France</h3>
      <p><strong>Defined Benefit:</strong> Revised total financial assets based on latest available data from the Banque de France (BdF), resulting in an increase of ~$80 bn for 2024</p>
      <p><strong>Defined Contribution:</strong> Revised total financial assets based on latest available data from the Banque de France (BdF), resulting in a decrease of ~$45 bn for 2024</p>
      <p><strong>Insurance Companies:</strong> Restated total financial assets based on latest available data from the Banque de France (BdF) and the European Insurance and Occupational Pensions Authority (EIOPA) to remove unit-linked assets already accounted for in Retail & HNW, resulting in a decrease of ~$545 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in a decrease of ~$85 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from the Banque de France (BdF), resulting in a decrease of ~$485 bn for 2024</p>
      <h3>Italy</h3>
      <p><strong>Insurance Companies:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in an increase of ~$25 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in an increase of ~$40 bn for 2024</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$40 bn for 2024</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Defined Benefit:</strong> Revised total financial assets based on latest available data from the Ministry of Human Resources and Social Security (MOHRSS), resulting in a decrease of ~$480 bn for 2024</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$460 bn for 2024</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Asset Management Association of China (AMAC), resulting in an increase of ~$450 bn for 2024</p>
      <h3>Hong Kong</h3>
      <p><strong>Insurance:</strong>Revised total financial assets based on latest available data from the Securities and Futures Commission of Hong Kong (SFC), resulting in an increase of ~$11 bn for 2024</p>
      <p><strong>Endowments and Foundations:</strong> Revised total financial assets based on latest available data from the Hong Kong Jockey Club, resulting in an increase of ~$13 bn for 2024</p>
      <h2>South East Asia</h2>
      <h3>Singapore</h3>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from the Monetary Authority of Singapore (MAS), resulting in a decrease of ~$42 bn for 2024</p>
      <h2>Rest of Asia-Pacific</h2>
      <h3>India</h3>
      <p><strong>Defined Contribution:</strong> Revised total financial assets based on latest available data from the Union Budget, resulting in a decrease of ~$26 bn for 2024</p>
       </>
  )
}

export function TfaPreviousCountryDetails() {
  return (
    <>
      <h2>LATAM</h2>
      <h3>Brazil</h3>
      <p><strong>Defined Benefit:</strong> Revised total financial assets based on latest available data from the National Superintendence of Supplementary Pensions (PREVIC), resulting in an increase of ~$10 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Brazil (BCB), resulting in an increase of ~$120 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Brazil (BCB), resulting in a decrease of ~$140 bn for 2023</p>
      <h3>Mexico</h3>
      <p><strong>Defined Contribution:</strong> Revised total financial assets based on latest available data from the National Commission for the Retirement Savings System (CONSAR), resulting in an increase of ~$10 bn for 2023</p>
      <h2>Western Europe</h2>
      <h3>Germany</h3>
      <p><strong>Insurance Companies:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$120 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$2 tn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$130 bn for 2023</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$2 tn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$330 bn for 2023</p>
      <h3>Italy</h3>
      <p><strong>Insurance Companies:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$70 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$40 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in a decrease of ~$20 bn for 2023</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in an increase of ~$30 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised total financial assets based on latest available data from the Central Bank of Italy, resulting in an increase of ~$10 bn for 2023</p>
      <h3>Netherlands</h3>
      <p><strong>Insurance Companies:</strong> Revised total financial assets based on latest available data from Statistics Netherlands (CBS), resulting in a decrease of ~$10 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from Statistics Netherlands (CBS), resulting in an increase of ~$1 tn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from Statistics Netherlands (CBS), resulting in a decrease of ~$40 bn for 2023</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from Statistics Netherlands (CBS), resulting in an increase of ~$330 bn for 2023</p>
      <h3>Spain</h3>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in an increase of ~$20 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from Eurostat, resulting in a decrease of ~$10 bn for 2023</p>
      <h3>United Kingdom</h3>
      <p><strong>Defined Benefit:</strong> Revised total financial assets based on latest available data from the Office for National Statistics (ONS), resulting in an increase of ~$40 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Office for National Statistics (ONS), resulting in an increase of ~$250 bn for 2023</p>
      <p><strong>Endowments &amp; Foundations:</strong> Revised total financial assets based on latest available data from the Office for National Statistics (ONS), resulting in an increase of ~$30 bn for 2023</p>
      <h2>Middle East &amp; Africa</h2>
      <h3>GCC Region</h3>
      <p><strong>Sovereign Wealth Funds:</strong> Revised total financial assets based on latest available data from Global SWF and supplementary research for individual funds in Saudi Arabia and the United Arab Emirates, resulting in an increase of ~$270 bn for 2023</p>
      <h2>East Asia</h2>
      <h3>China</h3>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from the Asset Management Association of China (AMAC), resulting in a decrease of ~$490 bn for 2023</p>
      <h3>Japan</h3>
      <p><strong>Defined Benefit:</strong> Revised total financial assets based on latest available data from the Government Pension Investment Fund (GPIF) and Pension Fund Association (PFA), resulting in a decrease of ~$90 bn for 2023</p>
      <p><strong>Financial Corporations:</strong> Revised total financial assets based on latest available data from Bank of Japan (BOJ) Statistics, resulting in an increase of ~$750 bn for 2023</p>
      <p><strong>Non-Financial Corporations:</strong> Revised total financial assets based on latest available data from Bank of Japan (BOJ) Statistics, resulting in a decrease of ~$260 bn for 2023</p>
      <p><strong>State Entities:</strong> Revised total financial assets based on latest available data from Bank of Japan (BOJ) Statistics, resulting in an increase of ~$310 bn for 2023</p>
    </>
  )
}

// --- Overview tab bodies ---

export function OverviewBankingPoolsContent() {
  return (
    <>
      <p>
        Banking Pools is a comprehensive market-sizing database that provides detailed insights into client-driven banking P&L at the product level. It covers a wide range of banking products and services, ensuring a standardized methodology for comparability across different markets and regions.
      </p>
      <h1>Scope</h1>
      <h2>Geographical coverage</h2>
      <p>56 countries</p>
      <h2>Metrics</h2>
      <p>Outstanding balances/transaction values</p>
      <p>Net interest income margins and fee margins</p>
      <p>Revenue before risk costs</p>
      <p>Risk cost margins</p>
      <p>Operating costs</p>
      <p>Pre-tax profits</p>
      <h2>Products</h2>
      <p>Retail banking products</p>
      <p>Wholesale transaction banking &amp; lending products – split by customer segments</p>
      <p>Asset management products</p>
      <h2>Years</h2>
      <p>2005-2024 Actual/ partial year estimates</p>
      <p>2025-2030 Forecast years</p>
    </>
  )
}

export function OverviewFullGranularityContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>29 countries (representing &gt;98% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (8 client segments)</p>
      <h2>Asset Classes (34)</h2>
      <h2>Vehicle (5)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2025</p>
      <p>Forecasted years 2026–2030</p>
      <h2>Metric</h2>
      <p>Assets Under Management (AUM)</p>
      <p>Annual AUM CAGR (%)</p>
      <p>Net Flows</p>
      <p>Annual Net Flow Effect (%)</p>
      <p>Capital Appreciation</p>
      <p>Annual Performance Effect (%)</p>
      <p>Net Revenues (Revenues from AUM)</p>
      <p>Net Inflow Revenues (Revenues from Net Inflows Only)</p>
      <p>Net Flow Revenues (Revenues from All Net Flows)</p>
      <h2>Scenario</h2>
      <p>Base Scenario</p>
      <p>Upside Scenario</p>
      <p>Downside Scenario</p>
    </>
  )
}
export function OverviewFullGranularityPreviousReleaseContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>29 countries (representing &gt;98% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (8 client segments)</p>
      <h2>Asset Classes (34)</h2>
      <h2>Vehicle (5)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2024</p>
      <p>Forecasted years 2025–2029</p>
      <h2>Metric</h2>
      <p>Assets Under Management (AUM)</p>
      <p>Annual AUM CAGR (%)</p>
      <p>Net Flows</p>
      <p>Annual Net Flow Effect (%)</p>
      <p>Capital Appreciation</p>
      <p>Annual Performance Effect (%)</p>
      <p>Net Revenues (Revenues from AUM)</p>
      <p>Net Inflow Revenues (Revenues from Net Inflows Only)</p>
      <p>Net Flow Revenues (Revenues from All Net Flows)</p>
      <h2>Scenario</h2>
      <p>Base Scenario</p>
      <p>Upside Scenario</p>
      <p>Downside Scenario</p>
    </>
  )
}

export function OverviewSegmentGranularityContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>42 countries (representing 100% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (total only)</p>
      <h2>Asset Classes (total only)</h2>
      <h2>Vehicle (total only)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2025</p>
      <p>Forecasted years 2026–2030</p>
      <h2>Metric</h2>
      <p>Assets Under Management (AUM)</p>
      <p>Annual AUM CAGR (%)</p>
      <p>Net Flows</p>
      <p>Annual Net Flow Effect (%)</p>
      <p>Capital Appreciation</p>
      <p>Annual Performance Effect (%)</p>
      <h2>Scenario</h2>
      <p>Base Scenario</p>
      <p>Upside Scenario</p>
      <p>Downside Scenario</p>
    </>
  )
}
export function OverviewSegmentGranularityPreviousReleaseContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>42 countries (representing 100% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (total only)</p>
      <h2>Asset Classes (total only)</h2>
      <h2>Vehicle (total only)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2024</p>
      <p>Forecasted years 2025–2029</p>
      <h2>Metric</h2>
      <p>Assets Under Management (AUM)</p>
      <p>Annual AUM CAGR (%)</p>
      <p>Net Flows</p>
      <p>Annual Net Flow Effect (%)</p>
      <p>Capital Appreciation</p>
      <p>Annual Performance Effect (%)</p>
      <h2>Scenario</h2>
      <p>Base Scenario</p>
      <p>Upside Scenario</p>
      <p>Downside Scenario</p>
    </>
  )
}

export function OverviewTotalFinancialAssetsContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>42 countries (representing 100% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (8 client segments)</p>
      <h2>Asset Classes (total only)</h2>
      <h2>Vehicle (total only)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2025</p>
      <h2>Metric</h2>
      <p>Total Financial Assets</p>
      <p>Unmanaged: Retail &amp; HNW Assets</p>
      <p>Internally Managed: Institutional Assets</p>
      <p>Externally Managed: Assets Under Management (AUM)</p>
    </>
  )
}
export function OverviewTotalFinancialAssetsPreviousReleaseContent() {
  return (
    <>
      <GgcDatasetIntro />
      <h1>Scope</h1>
      <h2>Geographies</h2>
      <p>42 countries (representing 100% of global AUM)</p>
      <h2>Client Segments</h2>
      <p>Total Retail &amp; HNW</p>
      <p>Total Institutional (8 client segments)</p>
      <h2>Asset Classes (total only)</h2>
      <h2>Vehicle (total only)</h2>
      <h2>Years</h2>
      <p>Historical years 2007–2024</p>
      <h2>Metric</h2>
      <p>Total Financial Assets</p>
      <p>Unmanaged: Retail &amp; HNW Assets</p>
      <p>Internally Managed: Institutional Assets</p>
      <p>Externally Managed: Assets Under Management (AUM)</p>
    </>
  )
}

export function OverviewUsBankingPoolsDefaultContent() {
  return (
    <>
      <p>
        US Banking Pools is a comprehensive market-sizing data-base that provides granular, product-level P&L insights. Database covers 70+ Retail banking, Commercial banking, and WAM products, ensuring a holistic view of the country&apos;s banking market
      </p>
      <h1>Scope</h1>
      <h2>Metrics</h2>
      <p>Originations</p>
      <p>Outstanding balances / transaction value</p>
      <p>Net interest income margins and fee margins</p>
      <p>Charge-offs</p>
      <p>Revenues</p>
      <h2>Products</h2>
      <p>Retail banking products</p>
      <p>Commercial banking products</p>
      <p>WAM products</p>
      <h2>Years</h2>
      <p>2020-2024 - Actuals / partial year estimates</p>
      <p>2025-2030 - Forecasts</p>
    </>
  )
}
